import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FormattedText from '../components/FormattedText';
import { CONTACT, SERVICES } from '../data';
import { fetchServiceBySlug } from '../lib/contentful/services/services';
import './DetailPage.css';

export default function ServiceDetail() {
  const { slug } = useParams();
  const localService = useMemo(() => SERVICES.find(item => item.id === slug), [slug]);
  const [service, setService] = useState(localService || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    async function loadService() {
      setLoading(true);
      setError('');

      try {
        const contentfulService = await fetchServiceBySlug(slug);
        if (!isActive) return;

        if (contentfulService) {
          setService(contentfulService);
          return;
        }

        if (localService) {
          setService(localService);
          return;
        }

        setService(null);
      } catch (err) {
        if (!isActive) return;
        setError(err instanceof Error ? err.message : 'Failed to load service.');
        setService(localService || null);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    loadService();

    return () => {
      isActive = false;
    };
  }, [slug, localService]);

  if (loading && !service) {
    return <div className="detail-404">Loading service...</div>;
  }
  if (!service) return <div className="detail-404">Service not found.</div>;
  const localServiceByTitle = SERVICES.find(item => item.title === service.title);
  const displayIcon = service.icon || localService?.icon || localServiceByTitle?.icon || '◈';

  return (
    <main className="detail-page">
      <div className="detail-hero">
        <div className="container">
          <Link to="/#services" className="detail-back">← Back to Services</Link>
          <span className="detail-icon">{displayIcon}</span>
          <h1 className="detail-title">{service.title}</h1>
        </div>
      </div>

      <div className="detail-body container">
        {error ? <p className="detail-load-warning">{error} Showing local fallback content.</p> : null}

        <div className="detail-content">
          <FormattedText as="p" className="detail-intro" text={service.detail?.intro || 'Not provided.'} />

          <div className="detail-highlights">
            <h2 className="detail-section-title">What's Included</h2>
            <ul className="detail-list">
              {(service.detail?.highlights || []).map((item, i) => (
                <li key={i} className="detail-list-item">
                  <span className="detail-list-mark">◆</span>
                  <FormattedText as="span" text={item} />
                </li>
              ))}
            </ul>
          </div>

          <div className="detail-outcome">
            <h2 className="detail-section-title">Expected Outcomes</h2>
            <FormattedText as="p" className="detail-outcome-text" text={service.detail?.outcome || 'Not provided.'} />
          </div>

          <div className="detail-cta">
            <a href={`mailto:${CONTACT.email}`} className="btn-primary">
              Discuss This Service
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
